import React from 'react';
import type { ProductExportQuery } from '@/api/handlers/resources/products/requests';
import { useExportProductsMutation } from '@/api/queries';
import { downloadBlob, xlsxBlobOf } from '@/utils';
import type { AppliedFilterTag } from '../productFilters/productFiltersMeta';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';
import {
    countExportedProducts,
    describeExportFilters,
    exportFailureNotice,
    formatExportedAt,
    productExportFilename,
    type ProductExportFilterLine,
    type ProductExportLabels,
    type ProductExportNotice,
} from './productExportMeta';
import { buildProductExportWorkbook } from './productExportWorkbook';

/**
 * What the list shows, which an export writes out (bos-app#120). The page
 * hands it to the export section; the section owns the run and its notice.
 */
export type ProductExportScope = {
    /** The list's request without the paging. */
    query: ProductExportQuery;
    /** The applied search, for the Filters sheet. */
    search: string;
    /** Whose stock the figures count, for the Filters sheet. */
    stockFigures: StockFiguresScope;
    /** The applied filters as the tags row shows them, spelt out in the Filters sheet. */
    appliedFilterTags: readonly AppliedFilterTag[];
    /**
     * How many products the list matched for these filters; `null` until it
     * knows, including while it still shows another filter set's rows.
     */
    totalCount: number | null;
};

export type ProductExportProps = { scope: ProductExportScope };

/** The Export Excel action and the notice it leaves. */
export type ProductExportState = {
    /** Nothing to export: the list matched nothing, or doesn't know yet. */
    isDisabled: boolean;
    /** From the press until the file is handed over or the run fails. */
    isExporting: boolean;
    /** The Filters sheet's lines, for the View to put into words. */
    filterLines: ProductExportFilterLine[];
    /**
     * Reads every matching row, writes the workbook and downloads it.
     * `labels` is the workbook's copy, which the View translates; the hook
     * stamps the Filters sheet with the time of the press.
     */
    onExport: (labels: ProductExportLabels) => void;
    /** How the last run ended; `null` until one does, or once dismissed. */
    notice: ProductExportNotice | null;
    onDismissNotice: () => void;
};

/**
 * Drives the toolbar's Export Excel (bos-app#120): `GET /products/export` for
 * the list's search, filters and branch across all pages, then a workbook
 * built in the browser (exceljs, loaded on demand) and downloaded as
 * `products-YYYY-MM-DD.xlsx`.
 *
 * One run at a time: a second press while one is under way is ignored, even
 * before the pressed state renders. A run exports the filters applied when it
 * was pressed; a change meanwhile doesn't reach it. It ends in a notice: the
 * product count and the file name, or a failure, with its own message when
 * bos-api refuses more than 20,000 rows.
 */
export const useProductExport = ({
    query,
    search,
    stockFigures,
    appliedFilterTags,
    totalCount,
}: ProductExportScope): ProductExportState => {
    const { mutateAsync: fetchRows } = useExportProductsMutation();
    const [isExporting, setIsExporting] = React.useState(false);
    const [notice, setNotice] = React.useState<ProductExportNotice | null>(
        null,
    );
    const isRunning = React.useRef(false);

    const isDisabled = totalCount === null || totalCount === 0;

    const filterLines = React.useMemo(
        () =>
            describeExportFilters({
                stockFigures,
                search,
                tags: appliedFilterTags,
            }),
        [appliedFilterTags, search, stockFigures],
    );

    const run = React.useCallback(
        async (labels: ProductExportLabels) => {
            const now = new Date();
            try {
                const { rows } = await fetchRows(query);
                const workbook = await buildProductExportWorkbook(
                    rows,
                    labels,
                    formatExportedAt(now),
                );
                const filename = productExportFilename(now);
                downloadBlob(await xlsxBlobOf(workbook), filename);
                setNotice({
                    kind: 'saved',
                    productCount: countExportedProducts(rows),
                    filename,
                });
            } catch (error) {
                // The console line is for whoever is debugging (the app has
                // no diagnostic adapter yet, see .agents/frontend.md); the notice is for
                // whoever pressed the button.
                console.error('[products] export failed:', error);
                setNotice({
                    kind: 'failed',
                    failure: exportFailureNotice(error),
                });
            } finally {
                isRunning.current = false;
                setIsExporting(false);
            }
        },
        [fetchRows, query],
    );

    const onExport = React.useCallback(
        (labels: ProductExportLabels) => {
            if (isDisabled || isRunning.current) {
                return;
            }
            isRunning.current = true;
            setIsExporting(true);
            setNotice(null);
            void run(labels);
        },
        [isDisabled, run],
    );

    const onDismissNotice = React.useCallback(() => setNotice(null), []);

    return {
        isDisabled,
        isExporting,
        filterLines,
        onExport,
        notice,
        onDismissNotice,
    };
};
